/**
 * What the WEB build of genz-engine can and cannot do — one source of truth for BOTH wasm
 * bindings (`WasmEngineBinding` on the main thread, `WasmBackedEngineBinding` inside the
 * worker), so a future reader cannot find two different answers to the same question.
 *
 * These are properties of HOW THE CRATE IS COMPILED, not of the running machine:
 * `scripts/build-wasm.sh` builds with `--no-default-features --features wasm`, which drops
 * `accel` (rayon needs OS threads wasm32-unknown-unknown does not have) and never enables
 * `gpu` or `ml`. So: one thread, no wgpu compositor, no ONNX segmenter. Answering "cpu / 1 / no"
 * is the truth, and it is why these need no worker round-trip — there is no question to ask the
 * worker whose answer could differ from this one.
 */
import type { CompositorInfo } from "./binding.js";

/** Rendered VERBATIM by the editor's compositor diagnostic (`MaskEditor.tsx`) — operator copy. */
export const WEB_COMPOSITOR_REASON = "web build — no GPU compositor";

export const webCompositorInfo = (): CompositorInfo => ({
  gpuSupported: false,
  gpuAvailable: false,
  lastPath: "cpu",
  lastFallbackReason: WEB_COMPOSITOR_REASON,
  adapterName: null,
  backend: null,
});

export const webBackendInfo = (): { backend: string; threads: number; gpuAdapter: string | null } =>
  ({ backend: "cpu", threads: 1, gpuAdapter: null });

/** The six web answers, as an object both bindings assign their members from. */
export const WEB_ENGINE_CAPABILITIES = {
  async compositorInfo(): Promise<CompositorInfo> { return webCompositorInfo(); },
  async backendInfo(): Promise<{ backend: string; threads: number; gpuAdapter: string | null }> {
    return webBackendInfo();
  },
  async hasMl(): Promise<boolean> { return false; },
  /** SYNTHESIS §6 C-12: no model is ever resident on web, so there is nothing to free. Resolving
   *  (rather than being absent) is what lets the editor's unmount path stop using `?.` as a
   *  platform test. */
  async releaseSegmentationModel(): Promise<void> { /* nothing resident */ },
  /** `false` = "no removal was running", which is always true here. NEVER `true`: the editor
   *  treats `true` as "I aborted your in-flight matte". */
  async cancelBackgroundRemoval(): Promise<boolean> { return false; },
  /** Accepted and recorded nowhere: the web compositor has no GPU path to switch to, and
   *  `backendInfo()` keeps saying `cpu` so the readout cannot claim otherwise. Present rather
   *  than absent so Settings can DISABLE the control with a reason instead of offering a toggle
   *  that silently does nothing. */
  async setRenderBackend(_backend: "auto" | "cpu"): Promise<void> { /* no GPU path on web */ },
} as const;
